import * as React from 'react';
import { useNavigate, useLocation } from 'react-router';
import { formatFilter, parseFilter } from '@chess/api-games/filter';
import type { PiletApi } from '@chess/platform-web';
import { reviewPath, stageOf, withStage } from '@chess/api-games';
import type { Phase, StageTurn } from '@chess/api-model';
import { MIN_ROWS, follow, formatDate, fromQuery, offsetOf, outcome, playerLabel, rangeLabel, rowNumber, rowsThatFit, steps, useGameList } from './store';
import { openingName } from './opening.ts';
import { ANALYSIS_API, type Analysis } from '@chess/api-analysis';
import { CHESS_API, type Chess } from '@chess/api-chess';
import { useGameStore, useStoreQuery } from '@chess/api-model/react';
import { returnTarget, useOpenAndReturn, useReturnFocus } from '@chess/api-navigation/react';
import { PHASES_API, endgameStartOf, openingEndOf, readerSideOf, type PhasesProvider } from '@chess/api-review';
import { stageScores, type StageScore, type StageScores } from './stage-scores.ts';
import './library-game-list.css';
import { STORE_API, type GameStore, type GameSummary, type ReviewPhases } from '@chess/api-model';

// The list of games of the last 30 days, one page of as many rows as fit on
// screen without a scrollbar (at least five), numbered from 1 across pages,
// read from the game store and kept current through its subscription: a game
// an importer stores shows up on its own. The end of the rows is a slot of
// its own, where an importer offers to fetch more. The opening is read from
// each game's PGN headers, which the store holds, and shown when they name one. When the game is
// scanned, the opening's and the middlegame's scores show in their own
// columns, read with the stage header's rules from the reader's side.
const columns = ['#', 'White', 'Black', 'Date', 'Opening', 'Opening score', 'Middlegame', 'Result'];
/** The stage focus chip's words. */
const STAGE_NAME: Record<Phase, string> = { opening: 'Opening', middlegame: 'Middlegame', ending: 'Ending' };

const RESULT_TAG: Record<ReturnType<typeof outcome>, string> = {
  won: 'tag tag-accent',
  lost: 'tag tag-outline',
  draw: 'tag tag-neutral',
  unknown: 'tag tag-neutral',
};

export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  // The opening's name from the PGN headers the store holds; nothing is read.
  const openings = new Map<string, string | null>();
  const openingOf = (store: GameStore | undefined, id: string): string | undefined => {
    if (!openings.has(id)) {
      const headers = store?.game(id)?.headers;
      if (headers === undefined) return undefined;
      openings.set(id, openingName(headers) ?? null);
    }
    return openings.get(id) ?? undefined;
  };

  // Stage scores by game id, computed once per game and again when its scan completes.
  const scores = new Map<string, StageScores>();
  const scoreListeners = new Map<string, Set<(s: StageScores) => void>>();
  const computing = new Set<string>();
  const compute = async (id: string) => {
    if (computing.has(id)) return;
    computing.add(id);
    try {
      const [store, chess] = await Promise.all([app.getApi<GameStore>(STORE_API), app.getApi<Chess>(CHESS_API)]);
      const scan = await store.scan(id);
      if (!scan) return;
      const record = await store.record(id);
      if (!record) return;
      const parsed = chess.parsePgn(record.pgn);
      const moveCount = parsed.moves.length;
      const provider = app.tryGetApi<PhasesProvider>(PHASES_API);
      const phases: ReviewPhases = provider
        ? await provider.phasesOf({ moves: parsed.moves, positions: parsed.positions })
        : { openingEnd: openingEndOf(moveCount, undefined), endgameStart: endgameStartOf(parsed.positions) };
      const result = stageScores(scan, { moveCount, positions: parsed.positions }, phases, readerSideOf(record));
      scores.set(id, result);
      scoreListeners.get(id)?.forEach((l) => l(result));
    } catch {
      // A row without scores shows the dash; the table must not fail for one game.
    } finally {
      computing.delete(id);
    }
  };
  void app.getApi<Analysis>(ANALYSIS_API).then((analysis) =>
    analysis.subscribe((e) => {
      if (e.type === 'complete') void compute(e.gameId);
    }),
  );
  const useStageScores = (id: string): StageScores => {
    const [value, setValue] = React.useState<StageScores>(scores.get(id) ?? {});
    React.useEffect(() => {
      const set = scoreListeners.get(id) ?? new Set();
      set.add(setValue);
      scoreListeners.set(id, set);
      if (scores.has(id)) setValue(scores.get(id)!);
      else void compute(id);
      return () => {
        set.delete(setValue);
      };
    }, [id]);
    return value;
  };
  /** The address a stage's decisive moment opens at: the position before the move, the stage's tab (2026-10-13). */
  const turnPath = (game: GameSummary, phase: Phase, turn: StageTurn) => withStage(`${reviewPath(game)}#ply=${Math.max(0, turn.ply - 1)}`, phase);
  /** "Decided by 23... Rxf4: their blunder, −3.1 pawns". */
  const turnTitle = (turn: StageTurn) =>
    `Decided by ${Math.ceil(turn.ply / 2)}${turn.ply % 2 ? '.' : '...'} ${turn.san}: ${turn.side === 'own' ? 'your' : 'their'} ${turn.kind}, ${turn.delta >= 5000 ? 'mate' : `−${(turn.delta / 100).toFixed(1)} pawns`}`;
  /** A stage's score; a link to the stage's decisive moment when the game has one. */
  const ScoreChip: React.FC<{ phase: Phase; score?: StageScore; href?: string; title?: string; onOpen?: () => void }> = ({ phase, score, href, title, onOpen }) =>
    !score ? null : href && onOpen ? (
      <a
        className={`game-stage-score game-stage-score-${score.tone} game-stage-score-link`}
        href={href}
        title={title}
        data-track="library.row.stage"
        data-track-value={phase}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }}
        onKeyDown={(e) => e.stopPropagation()}>
        {score.text}
      </a>
    ) : (
      <span className={`game-stage-score game-stage-score-${score.tone}`}>{score.text}</span>
    );

  const Row: React.FC<{ game: GameSummary; number: number; store?: GameStore; focus?: Phase }> = ({ game, number, store, focus }) => {
    const opening = openingOf(store, game.id);
    const stage = useStageScores(game.id);
    // Each stage's decisive moment, from the store's facts: where a stage's score opens the game.
    const turns = useStoreQuery(getApi, (s) => s.game(game.id)?.facts?.stageTurns, [game.id]);
    const openAndReturn = useOpenAndReturn();
    const chip = (phase: Phase, score?: StageScore) => {
      const turn = turns?.[phase];
      return (
        <ScoreChip
          phase={phase}
          score={score}
          href={turn ? turnPath(game, phase, turn) : undefined}
          title={turn ? turnTitle(turn) : undefined}
          onOpen={turn ? () => openAndReturn(turnPath(game, phase, turn), game.id) : undefined}
        />
      );
    };
    // The library's stage focus (`?stage=`) travels to the review, which opens
    // on that stage; with a focus the row opens like its stage's score: at the
    // stage's decisive moment when the game has one (2026-10-13).
    const focusTurn = focus ? turns?.[focus] : undefined;
    const open = () => openAndReturn(focus && focusTurn ? turnPath(game, focus, focusTurn) : withStage(reviewPath(game), focus), game.id);
    const how = outcome(game);
    return (
      <tr
        {...returnTarget(game.id)}
        className={`game-row game-${how}`}
        tabIndex={0}
        data-track="library.row"
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        }}>
        <td className="game-number">{number}</td>
        <td>{playerLabel(game.white)}</td>
        <td>{playerLabel(game.black)}</td>
        <td>
          <time dateTime={game.playedAt}>{formatDate(game.playedAt)}</time>
        </td>
        <td className="game-opening" title={opening}>
          <span className="game-opening-name">{opening ?? <span className="panel-empty">—</span>}</span>
        </td>
        <td className="game-opening-score">{stage.opening ? chip('opening', stage.opening) : <span className="panel-empty">—</span>}</td>
        <td className="game-middlegame">{stage.middlegame ? chip('middlegame', stage.middlegame) : <span className="panel-empty">—</span>}</td>
        <td>
          <span className={RESULT_TAG[how]} title={how}>
            {game.result === '1/2-1/2' ? '½-½' : game.result}
          </span>
        </td>
      </tr>
    );
  };

  /** Page controls under the last row: first, previous, the rows shown, next, last. */
  const Pager: React.FC<{ offset: number; shown: number; size: number; total: number; onGo: (offset: number) => void }> = ({
    offset,
    shown,
    size,
    total,
    onGo,
  }) => {
    const to = steps(offset, size, total);
    const atStart = offset === 0;
    const atEnd = offset + shown >= total;
    return (
      <nav className="library-game-list-pager" aria-label="Pages">
        <button type="button" className="btn btn-ghost" data-track="library.page" data-track-value="first" onClick={() => onGo(to.first)} disabled={atStart} aria-label="First page" title="First page">
          «
        </button>
        <button type="button" className="btn btn-ghost" data-track="library.page" data-track-value="previous" onClick={() => onGo(to.previous)} disabled={atStart} aria-label="Previous page" title="Previous page">
          ‹
        </button>
        <span className="library-game-list-page">{rangeLabel(offset, shown, total)}</span>
        <button type="button" className="btn btn-ghost" data-track="library.page" data-track-value="next" onClick={() => onGo(to.next)} disabled={atEnd} aria-label="Next page" title="Next page">
          ›
        </button>
        <button type="button" className="btn btn-ghost" data-track="library.page" data-track-value="last" onClick={() => onGo(to.last)} disabled={atEnd} aria-label="Last page" title="Last page">
          »
        </button>
      </nav>
    );
  };

  /**
   * How many rows fit in the rows area without a scrollbar: its height less
   * the table head, over one row's height (a rendered row's, or the head's
   * before any row exists). Recomputed when the area resizes, debounced, and
   * once the first rows render, when their real height replaces the guess.
   */
  const measureRows = (el: HTMLElement): number => {
    const head = el.querySelector('thead')?.getBoundingClientRect().height ?? 0;
    const row = el.querySelector('tbody tr.game-row')?.getBoundingClientRect().height || head;
    return rowsThatFit(el.clientHeight - head, row);
  };
  const useRowsThatFit = (area: React.RefObject<HTMLDivElement | null>, rendered: boolean): number | undefined => {
    const [fit, setFit] = React.useState<number | undefined>(undefined);
    React.useLayoutEffect(() => {
      if (rendered && area.current) setFit(measureRows(area.current));
    }, [area, rendered]);
    React.useEffect(() => {
      const el = area.current;
      if (!el) return;
      const measure = () => setFit(measureRows(el));
      let timer: ReturnType<typeof setTimeout> | undefined;
      const observer = new ResizeObserver(() => {
        clearTimeout(timer);
        timer = setTimeout(measure, 100);
      });
      observer.observe(el);
      measure();
      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }, [area]);
    return fit;
  };

  const GameList: React.FC = () => {
    const games = useGameList((s) => s.games);
    const offset = useGameList((s) => s.offset);
    const size = useGameList((s) => s.size);
    const total = useGameList((s) => s.total);
    const status = useGameList((s) => s.status);
    const error = useGameList((s) => s.error);
    const api = useGameStore(getApi);
    // Back from a game's review: its row is focused and highlighted (the page is in the URL).
    useReturnFocus(status === 'ready' && games.length > 0);
    const area = React.useRef<HTMLDivElement>(null);
    const fit = useRowsThatFit(area, games.length > 0);
    // The filter and the page are the library URL's query: the filter's
    // parameters (see `@chess/api-games/filter`), written by the filter plugin
    // or by a link into the library, and `from`, the first row from 1, written
    // here. The filter plugin writes only its own parameters, so a filter
    // change drops `from` and the list starts again from the first row. A
    // resize keeps the first row and changes only how many follow it.
    const navigate = useNavigate();
    const location = useLocation();
    const filterKey = formatFilter(parseFilter(location.search));
    const wanted = offsetOf(location.search);
    // The stage focus, `?stage=` (2026-10-13): set by a stage screen's link
    // into the library, kept through paging and filtering, passed on to each
    // game's review, shown as a chip beside the title and dropped by it.
    const focus = stageOf(location.search);
    const go = React.useCallback(
      (to: number) =>
        navigate({ pathname: location.pathname, search: withStage(fromQuery(filterKey, to), focus), hash: location.hash }, { replace: true, preventScrollReset: true }),
      [navigate, location.pathname, location.hash, filterKey, focus],
    );
    const dropFocus = () =>
      navigate({ pathname: location.pathname, search: withStage(location.search, undefined), hash: location.hash }, { replace: true, preventScrollReset: true });
    // Follows the store once it and the room are known, then loads
    // again whenever the filter, the first row or the room changes. The key
    // is what the list was last loaded for, so nothing loads twice.
    const following = api !== undefined && fit !== undefined;
    const loaded = React.useRef('');
    React.useEffect(() => {
      if (!api || fit === undefined) return;
      loaded.current = `${filterKey}|${wanted}|${fit}`;
      return follow(api, parseFilter(filterKey), wanted, fit);
    }, [following]); // eslint-disable-line react-hooks/exhaustive-deps
    React.useEffect(() => {
      if (!api || fit === undefined) return;
      const key = `${filterKey}|${wanted}|${fit}`;
      if (key === loaded.current) return;
      loaded.current = key;
      useGameList.getState().load(api, parseFilter(filterKey), wanted, fit);
    }, [api, filterKey, wanted, fit]);
    // A first row past the end (games removed, a link to rows that no longer exist) shows the last page; the URL follows.
    React.useEffect(() => {
      if (status === 'ready' && offset !== wanted) go(offset);
    }, [status, offset]); // eslint-disable-line react-hooks/exhaustive-deps
    return (
      <section className="panel library-game-list">
        <div className="library-game-list-head">
          <h2>
            Games
            {status === 'ready' && total > 0 ? ` · ${total}` : ''}
          </h2>
          {focus && (
            <button
              type="button"
              className="library-stage-focus"
              title={`A game opens on its ${focus}; click to open games on their first move instead`}
              aria-label={`Opening games on the ${focus}; remove`}
              data-track="library.stage-chip"
              data-track-value={focus}
              onClick={dropFocus}>
              {STAGE_NAME[focus]}
              <span aria-hidden="true"> ×</span>
            </button>
          )}
        </div>
        <div className="library-game-list-rows" ref={area} style={{ ['--min-rows' as string]: MIN_ROWS }}>
          <table className="table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c} className={c === '#' ? 'game-number' : undefined}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {games.map((game, i) => (
                <Row key={game.id} game={game} number={rowNumber(offset, i)} store={api} focus={focus} />
              ))}
              {games.length === 0 && (
                <tr>
                  <td colSpan={columns.length}>
                    <p className="panel-empty">
                      {status === 'loading' && 'Loading…'}
                      {status === 'error' && `Could not read the library: ${error}`}
                      {status === 'ready' &&
                        (!filterKey
                          ? 'No games in the last 30 days. Import them from the toolbar.'
                          : parseFilter(filterKey).blunder
                            ? 'No scanned game matches. Only scanned games have blunders; scan more to find them.'
                            : 'No games match the filter.')}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {status === 'ready' && total > 0 && <Pager offset={offset} shown={games.length} size={size} total={total} onGo={go} />}
        <div className="library-game-list-end">
          <app.Extension name="library.gamesEnd" />
        </div>
      </section>
    );
  };
  app.registerExtension('library.games', GameList);
}
